import { useEffect } from "react";
import { useToken } from "../../../server/auth/use-token";
import { useFlag } from "../../../server/optimizely";
import { serviceURL } from "../../../utils/service-url";

import type { UUID } from "../../../utils/uuid";

// Close codes sent by segment-engine.
export const TOKEN_EXPIRED = 4001;
export const FORBIDDEN = 4003;

// The engine answers "ping" with "pong" without waking, so this keeps idle sockets open for free.
export const PING_INTERVAL = 30_000;

export const segmentEngineSocketURL = (segmentId: UUID): URL => {
  const url = new URL(segmentId, serviceURL("segment-engine"));
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url;
};

/**
 * Opens a WebSocket to the segment-engine Durable Object for this segment
 */
export const useSegmentEngineSocket = (segmentId: UUID | null): void => {
  const [enabled] = useFlag("cha_4571_segment_engine_websocket");
  const getToken = useToken();

  useEffect(() => {
    if (!enabled || !segmentId) return undefined;

    let closed = false;
    let socket: WebSocket | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;

    const connect = async () => {
      const token = await getToken();
      if (closed) return;

      // Browsers can't set Authorization on a WebSocket, so the token goes as a subprotocol.
      socket = new WebSocket(segmentEngineSocketURL(segmentId), ["bearer", token]);
      socket.addEventListener("open", () => {
        ping = setInterval(() => socket?.send("ping"), PING_INTERVAL);
      });
      socket.addEventListener("message", (event) => {
        if (event.data !== "pong") console.log("segment-engine:", event.data);
      });
      socket.addEventListener("error", (event) =>
        console.error("segment-engine socket error", event),
      );
      socket.addEventListener("close", (event) => {
        clearInterval(ping);
        if (closed) return;
        if (event.code === TOKEN_EXPIRED) void connect();
        else if (event.code === FORBIDDEN) console.error("segment-engine: forbidden");
      });
    };

    void connect();

    return () => {
      closed = true;
      clearInterval(ping);
      socket?.close();
    };
  }, [enabled, segmentId, getToken]);
};
